import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { uploadSmart } from "@/lib/upload";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { ArrowLeft, Camera, Trash2, AlertTriangle, Send, Loader2, ChevronDown, X, Upload, CheckCircle2, Undo2, ChevronLeft, ChevronRight, Filter, Images, GalleryHorizontal, Pencil, Search as SearchIcon } from "lucide-react";

const MONTHS_TR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const dayLabel = (iso) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS_TR[d.getMonth()]} ${d.getFullYear()}`;
};
const timeLabel = (iso) => new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
const dateTimeLabel = (iso) => `${dayLabel(iso)} ${timeLabel(iso)}`;

const Lightbox = ({ box, onClose, onNav }) => {
  const f = box.files[box.idx];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onNav(-1);
      else if (e.key === "ArrowRight") onNav(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNav]);
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="photo-lightbox" onClick={onClose}>
      <button type="button" onClick={onClose} data-testid="lightbox-close" className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-10"><X size={26} /></button>
      <div className="absolute top-4 left-4 text-white/80 text-sm z-10">
        <div className="font-semibold">{box.title}</div>
        <div className="text-xs text-white/50 mt-0.5">{box.idx + 1} / {box.files.length}{f?.subtitle ? ` · ${f.subtitle}` : f?.original_filename ? ` · ${f.original_filename}` : ""}</div>
      </div>
      {box.files.length > 1 && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onNav(-1); }} data-testid="lightbox-prev" className="absolute left-2 sm:left-4 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-2.5 transition-colors duration-150 z-10"><ChevronLeft size={26} /></button>
      )}
      <img src={f?.url} alt={f?.original_filename || "foto"} onClick={(e) => e.stopPropagation()} className="max-h-[88vh] max-w-[92vw] object-contain select-none" data-testid="lightbox-image" />
      {box.files.length > 1 && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onNav(1); }} data-testid="lightbox-next" className="absolute right-2 sm:right-4 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-2.5 transition-colors duration-150 z-10"><ChevronRight size={26} /></button>
      )}
    </div>
  );
};

const MultiPick = ({ placeholder, items, sel, setSel, testid, tagCls }) => {
  const [q, setQ] = useState("");
  const list = items.filter((r) => `${r.name} ${r.email || ""}`.toLocaleLowerCase("tr").includes(q.toLocaleLowerCase("tr")));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className="h-11 border border-slate-200 px-3 text-sm bg-white flex items-center justify-between gap-2 text-slate-600">
          {sel.length > 0 ? `${sel.length} seçildi` : placeholder}
          <ChevronDown size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="rounded-none p-0 w-[320px]" align="start">
        <div className="p-2 border-b border-slate-100">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara..." data-testid={`${testid}-search`} className="rounded-none h-8 text-xs border-slate-200" />
        </div>
        <div className="max-h-[240px] overflow-y-auto divide-y divide-slate-100">
          {list.length === 0 && <div className="px-3 py-4 text-xs text-slate-400 text-center">Sonuç yok.</div>}
          {list.map((r) => (
            <label key={r.id} className={`flex items-center gap-2 px-3 py-2 text-sm ${r.no_email ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50"}`}>
              <Checkbox checked={sel.includes(r.id)} disabled={r.no_email} onCheckedChange={(c) => setSel((s) => (c ? [...s, r.id] : s.filter((x) => x !== r.id)))} className="rounded-none" data-testid={`${testid}-opt-${r.id}`} />
              <span className="flex-1 truncate">{r.name}</span>
              {r.tag && <span className={`text-[9px] font-bold px-1.5 py-0.5 border ${(tagCls && tagCls[r.tag]) || "bg-slate-100 text-slate-500 border-slate-200"}`}>{r.tag}</span>}
              {r.no_email ? <span className="text-[9px] font-bold text-red-400">e-posta yok</span> : <span className="text-[10px] text-slate-400 truncate max-w-[90px]">{r.email}</span>}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const CommentThread = ({ ph, onAdd }) => {
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const t = txt.trim();
    if (!t || busy) return;
    setBusy(true);
    const ok = await onAdd(ph, t);
    setBusy(false);
    if (ok) setTxt("");
  };
  return (
    <div className="mt-3 border-t border-slate-100 pt-2" data-testid={`photo-comments-${ph.id}`}>
      {(ph.comments || []).length > 0 && (
        <div className="space-y-1.5 mb-2">
          {(ph.comments || []).map((c) => (
            <div key={c.id} className={`text-xs px-2.5 py-1.5 border ${c.created_by?.role === "superadmin" ? "bg-indigo-50 border-indigo-100" : "bg-slate-50 border-slate-100"}`} data-testid={`photo-comment-${c.id}`}>
              <span className="font-bold text-slate-700">{c.created_by?.name}{c.created_by?.role === "superadmin" ? " · Yönetim" : ""}</span>
              <span className="text-slate-400 ml-1.5">{dateTimeLabel(c.created_at)}</span>
              <div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{c.text}</div>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Mesaj yaz..." data-testid={`photo-comment-input-${ph.id}`} className="flex-1 h-9 border border-slate-200 px-2.5 text-xs focus:outline-none focus:border-slate-900" />
        <button type="button" disabled={busy} onClick={send} data-testid={`photo-comment-send-${ph.id}`} className="h-9 px-3 bg-slate-900 text-white hover:bg-slate-700 transition-colors duration-150 disabled:opacity-50"><Send size={13} /></button>
      </div>
    </div>
  );
};

export default function FieldPhotosPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const backPath = user?.role === "superadmin" ? "/admin/dashboard" : user?.role === "uretici" ? "/uretici" : "/atayici";
  const [photos, setPhotos] = useState([]);
  const [targets, setTargets] = useState([]);
  const [recipients, setRecipients] = useState({ users: [], sems: [], companies: [] });
  const [loaded, setLoaded] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [files, setFiles] = useState([]);
  const [isIssue, setIsIssue] = useState(false);
  const [issueText, setIssueText] = useState("");
  const [recSel, setRecSel] = useState([]);
  const [semSel, setSemSel] = useState([]);
  const [firmSel, setFirmSel] = useState([]);
  const [issueEdit, setIssueEdit] = useState(null);
  const [extraName, setExtraName] = useState("");
  const [extraEmail, setExtraEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [filterProject, setFilterProject] = useState(sp.get("project") || "");
  const [onlyIssues, setOnlyIssues] = useState(sp.get("sorun") === "1");
  const [compareMode, setCompareMode] = useState(false);
  const [box, setBox] = useState(null);
  const [tab, setTab] = useState(sp.get("sorun") === "1" ? "issues" : (sp.get("project") || user?.role === "superadmin") ? "photos" : "add");
  const [search, setSearch] = useState("");

  const load = () => api.get("/field-photos").then((r) => { setPhotos(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  useEffect(() => {
    load();
    api.get("/field-photos/targets").then((r) => setTargets(r.data)).catch(() => {});
    api.get("/field-photos/recipients").then((r) => setRecipients(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);
  const openSelBox = (i) => setBox({ files: files.map((f, j) => ({ id: j, url: previews[j], original_filename: f.name })), idx: i, title: "Seçilen Fotoğraflar (önizleme)" });

  const send = async () => {
    if (!projectId) return toast.error("Proje seçin");
    if (files.length === 0) return toast.error("En az bir fotoğraf seçin");
    if (isIssue && !issueText.trim()) return toast.error("Sorun açıklaması yazın");
    if (extraEmail.trim() && !extraEmail.includes("@")) return toast.error("Geçerli bir e-posta girin");
    setBusy(true);
    try {
      const up = await uploadSmart(files, null, { compressImages: true });
      const r = await api.post("/field-photos", {
        project_id: projectId,
        file_ids: up.map((f) => f.id),
        is_issue: isIssue,
        issue_text: isIssue ? issueText : null,
        recipient_ids: [...recSel, ...semSel, ...firmSel],
        extra_name: extraName,
        extra_email: extraEmail.trim() || null,
      });
      toast.success(r.data.sent_count > 0 ? `Fotoğraflar kaydedildi — ${r.data.sent_count} kişiye e-posta gönderildi` : "Fotoğraflar kaydedildi");
      setFiles([]); setIsIssue(false); setIssueText(""); setRecSel([]); setSemSel([]); setFirmSel([]); setExtraName(""); setExtraEmail("");
      load();
      api.get("/field-photos/targets").then((rr) => setTargets(rr.data)).catch(() => {});
    } catch (e) {
      toast.error(apiError(e) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async (ph) => {
    try {
      await api.delete(`/field-photos/${ph.id}`);
      setPhotos((xs) => xs.filter((x) => x.id !== ph.id));
      toast.success("Kayıt silindi");
    } catch (e) { toast.error(apiError(e)); }
  };

  const resolve = async (ph, val) => {
    try {
      const r = await api.patch(`/field-photos/${ph.id}/resolve`, { resolved: val });
      setPhotos((xs) => xs.map((x) => (x.id === ph.id ? { ...x, resolved: r.data.resolved, resolved_by: r.data.resolved_by, resolved_at: r.data.resolved_at } : x)));
      setTargets((ts) => ts.map((t) => (t.id === ph.project_id ? { ...t, open_issues: Math.max(0, (t.open_issues || 0) + (val ? -1 : 1)) } : t)));
      toast.success(val ? (r.data.email_sent ? "Çözüldü işaretlendi — sorunu bildirene e-posta gönderildi" : "Çözüldü olarak işaretlendi") : "Çözüldü işareti kaldırıldı");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveIssue = async () => {
    if (!issueEdit?.text?.trim()) return toast.error("Sorun açıklaması boş olamaz");
    try {
      const r = await api.patch(`/field-photos/${issueEdit.id}/issue`, { issue_text: issueEdit.text.trim() });
      setPhotos((xs) => xs.map((x) => (x.id === issueEdit.id ? { ...x, issue_text: r.data.issue_text, is_issue: true } : x)));
      setIssueEdit(null);
      toast.success(r.data.email_sent > 0 ? `Sorun güncellendi — ${r.data.email_sent} kişiye e-posta gönderildi` : "Sorun güncellendi");
    } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = useMemo(() => {
    const q = search.toLocaleLowerCase("tr").trim();
    return photos.filter((ph) => {
      if (filterProject && ph.project_id !== filterProject) return false;
      if (tab === "issues" && !ph.is_issue) return false;
      if (onlyIssues && !ph.is_issue) return false;
      if (!q) return true;
      return [ph.project?.name, ph.project?.stand_no, ph.created_by?.name, ph.created_by?.company, ph.issue_text]
        .filter(Boolean).join(" ").toLocaleLowerCase("tr").includes(q);
    });
  }, [photos, filterProject, onlyIssues, search, tab]);

  const openCount = useMemo(() => photos.filter((p) => p.is_issue && !p.resolved).length, [photos]);

  const addComment = async (ph, text) => {
    try {
      const { data } = await api.post(`/field-photos/${ph.id}/comments`, { text });
      setPhotos((ps) => ps.map((x) => (x.id === ph.id ? { ...x, comments: [...(x.comments || []), data] } : x)));
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    }
  };
  const resolvedCount = useMemo(() => photos.filter((p) => p.is_issue && p.resolved).length, [photos]);
  const totalPhotos = useMemo(() => photos.reduce((a, p) => a + (p.file_ids?.length || p.files?.length || 0), 0), [photos]);

  const groups = useMemo(() => {
    const m = new Map();
    filtered.forEach((ph) => {
      const key = dayLabel(ph.created_at);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(ph);
    });
    return [...m.entries()];
  }, [filtered]);

  const stripDays = useMemo(() => {
    if (!filterProject) return [];
    const m = new Map();
    photos
      .filter((p) => p.project_id === filterProject)
      .slice()
      .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)))
      .forEach((ph) => {
        const k = dayLabel(ph.created_at);
        if (!m.has(k)) m.set(k, []);
        m.get(k).push(ph);
      });
    return [...m.entries()];
  }, [photos, filterProject]);

  const stripFlat = useMemo(() => stripDays.flatMap(([day, items]) =>
    items.flatMap((ph) => (ph.files || []).map((f) => ({ ...f, subtitle: `${day} ${timeLabel(ph.created_at)}` })))
  ), [stripDays]);

  const stripProjectName = useMemo(() => {
    const t = targets.find((x) => x.id === filterProject);
    return t ? `${t.name}${t.stand_no ? ` — ${t.stand_no}` : ""}` : "";
  }, [targets, filterProject]);

  const openStripBox = (flatIdx) => setBox({ files: stripFlat, idx: flatIdx, title: stripProjectName });

  const openBox = (ph, idx) => {
    const title = `${ph.project?.name || "—"}${ph.project?.stand_no ? ` — ${ph.project.stand_no}` : ""}`;
    setBox({ files: ph.files || [], idx, title });
  };
  const navBox = useCallback((dir) => {
    setBox((b) => (b ? { ...b, idx: (b.idx + dir + b.files.length) % b.files.length } : b));
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="field-photos-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><Camera size={10} /> Saha Fotoğrafları</div>
          </div>
          <button onClick={() => navigate(backPath)} data-testid="field-photos-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-900 px-3 py-2 transition-colors duration-150">
            <ArrowLeft size={14} /> Geri Dön
          </button>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white border border-red-200 p-4" data-testid="kpi-open-issues">
            <div className="text-[10px] uppercase tracking-wider text-red-500 font-bold flex items-center gap-1"><AlertTriangle size={11} /> Açık Sorun</div>
            <div className="font-heading text-3xl font-extrabold text-red-600 mt-1 tabular-nums">{openCount}</div>
          </div>
          <div className="bg-white border border-emerald-200 p-4" data-testid="kpi-resolved-issues">
            <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold flex items-center gap-1"><CheckCircle2 size={11} /> Çözülen Sorun</div>
            <div className="font-heading text-3xl font-extrabold text-emerald-600 mt-1 tabular-nums">{resolvedCount}</div>
          </div>
          <div className="bg-white border border-slate-200 p-4" data-testid="kpi-total-photos">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1"><Images size={11} /> Toplam Fotoğraf</div>
            <div className="font-heading text-3xl font-extrabold text-slate-800 mt-1 tabular-nums">{totalPhotos}</div>
          </div>
        </div>

        <div className="flex bg-white border border-slate-200 w-fit" data-testid="field-photos-tabs">
          <button type="button" onClick={() => setTab("add")} data-testid="field-photos-tab-add" className={`px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2 transition-colors duration-150 ${tab === "add" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>
            <Camera size={14} /> Fotoğraf Ekle
          </button>
          <button type="button" onClick={() => setTab("photos")} data-testid="field-photos-tab-photos" className={`px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2 transition-colors duration-150 ${tab === "photos" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>
            <Images size={14} /> Fotoğraflar <span className="text-[10px] font-extrabold tabular-nums opacity-70">{totalPhotos}</span>
          </button>
          <button type="button" onClick={() => setTab("issues")} data-testid="field-photos-tab-issues" className={`px-5 py-2.5 text-sm font-bold inline-flex items-center gap-2 transition-colors duration-150 ${tab === "issues" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>
            <AlertTriangle size={14} className={tab === "issues" ? "" : "text-amber-500"} /> Sorunlar <span className={`text-[10px] font-extrabold tabular-nums ${tab === "issues" ? "opacity-70" : openCount > 0 ? "text-red-600" : "opacity-70"}`}>{openCount}</span>
          </button>
        </div>

        {tab === "add" && (
        <section className="bg-white border border-slate-200 p-5 space-y-4" data-testid="field-photo-form">
          <h1 className="font-heading text-xl font-extrabold tracking-tight flex items-center gap-2"><Camera size={18} /> Sahadan Fotoğraf Ekle</h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="field-photo-project" className="h-11 border border-slate-200 px-3 text-sm bg-white">
              <option value="">Proje / stand seçin...</option>
              {targets.map((t) => <option key={t.id} value={t.id}>{t.name}{t.stand_no ? ` — ${t.stand_no}` : ""}</option>)}
            </select>
            <label className="h-11 border border-dashed border-slate-300 hover:border-slate-500 bg-slate-50 px-3 flex items-center gap-2 text-sm text-slate-600 cursor-pointer transition-colors duration-150">
              <Upload size={15} />
              {files.length > 0 ? `${files.length} fotoğraf seçildi` : "Fotoğraf seç (çoklu)"}
              <input type="file" multiple accept="image/*" capture="environment" className="hidden" data-testid="field-photo-input" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </label>
          </div>
          {files.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="selected-previews">
              {files.map((f, i) => (
                <div key={i} className="relative group aspect-square bg-slate-100 overflow-hidden border border-slate-200">
                  <button type="button" onClick={() => openSelBox(i)} data-testid={`selected-preview-${i}`} className="w-full h-full cursor-zoom-in">
                    <img src={previews[i]} alt={f.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                  </button>
                  <button type="button" onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))} data-testid={`selected-remove-${i}`} className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white p-1 transition-colors duration-150"><X size={11} /></button>
                  <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[9px] px-1.5 py-0.5 truncate">{f.name}</span>
                </div>
              ))}
            </div>
          )}
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
            <Checkbox checked={isIssue} onCheckedChange={(c) => setIsIssue(!!c)} className="rounded-none" data-testid="field-photo-issue-check" />
            <AlertTriangle size={14} className="text-amber-500" /> Sorun Bildir
          </label>
          {isIssue && (
            <Textarea value={issueText} onChange={(e) => setIssueText(e.target.value)} placeholder="Sorunu yazın..." data-testid="field-photo-issue-text" className="rounded-none border-amber-300 bg-amber-50/40 min-h-[80px]" />
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MultiPick placeholder="Kullanıcıları seç" items={recipients.users || []} sel={recSel} setSel={setRecSel} testid="field-photo-recipients" tagCls={{ CM: "bg-sky-500/10 text-sky-600 border-sky-500/20" }} />
            <MultiPick placeholder="SEM seç" items={recipients.sems || []} sel={semSel} setSel={setSemSel} testid="field-photo-sems" tagCls={{ SEM: "bg-violet-500/10 text-violet-600 border-violet-500/20" }} />
            <MultiPick placeholder="Firmaları seç" items={recipients.companies || []} sel={firmSel} setSel={setFirmSel} testid="field-photo-companies" tagCls={{ "Üretici": "bg-amber-500/10 text-amber-600 border-amber-500/20", "Tedarikçi": "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" }} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input value={extraName} onChange={(e) => setExtraName(e.target.value)} placeholder="Ekstra kişi adı (ops.)" data-testid="field-photo-extra-name" className="rounded-none border-slate-200 h-11" />
            <Input value={extraEmail} onChange={(e) => setExtraEmail(e.target.value)} placeholder="Ekstra e-posta (ops.)" data-testid="field-photo-extra-email" className="rounded-none border-slate-200 h-11" />
          </div>
          <Button onClick={send} disabled={busy} data-testid="field-photo-send" className="rounded-none h-11 px-6 bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Gönder
          </Button>
        </section>
        )}

        {(tab === "photos" || tab === "issues") && (
        <>
        <section className="bg-white border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap" data-testid="field-photo-filters">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1.5 shrink-0"><Filter size={12} /> Filtrele</div>
          <div className="relative sm:w-[240px]">
            <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ara: firma, stand, kişi, sorun..."
              data-testid="field-photo-search"
              className="w-full h-10 border border-slate-200 bg-white pl-8 pr-8 text-sm focus:outline-none focus:border-slate-900"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} data-testid="field-photo-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900"><X size={13} /></button>
            )}
          </div>
          <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} data-testid="field-photo-filter-project" className="h-10 border border-slate-200 px-3 text-sm bg-white sm:w-[320px]">
            <option value="">Tüm projeler</option>
            {targets.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}{t.stand_no ? ` — ${t.stand_no}` : ""}{t.open_issues > 0 ? ` (${t.open_issues} açık sorun)` : ""}
              </option>
            ))}
          </select>
          {tab !== "issues" && (
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
            <Checkbox checked={onlyIssues} onCheckedChange={(c) => setOnlyIssues(!!c)} className="rounded-none" data-testid="field-photo-filter-issues" />
            <AlertTriangle size={14} className="text-amber-500" /> Sadece sorun bildirimleri
          </label>
          )}
          {(filterProject || onlyIssues || search) && (
            <button type="button" onClick={() => { setFilterProject(""); setOnlyIssues(false); setCompareMode(false); setSearch(""); }} data-testid="field-photo-filter-clear" className="text-xs text-slate-400 hover:text-slate-700 underline">Temizle</button>
          )}
          <button
            type="button"
            onClick={() => {
              if (!filterProject) return toast.error("Karşılaştırma için önce bir proje seçin");
              setCompareMode((c) => !c);
            }}
            data-testid="field-photo-compare-toggle"
            className={`sm:ml-auto inline-flex items-center gap-1.5 text-xs font-bold border px-3 py-2 transition-colors duration-150 ${compareMode ? "bg-slate-900 text-white border-slate-900" : filterProject ? "bg-white text-slate-700 border-slate-300 hover:border-slate-900" : "bg-white text-slate-300 border-slate-200 cursor-not-allowed"}`}
          >
            <GalleryHorizontal size={13} /> Kronolojik Şerit
          </button>
        </section>

        {compareMode && filterProject ? (
          <section className="bg-white border border-slate-200 p-5 space-y-4" data-testid="compare-strip">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-heading text-lg font-extrabold tracking-tight flex items-center gap-2">
                <GalleryHorizontal size={16} /> {stripProjectName}
              </h2>
              <span className="text-xs text-slate-400">{stripDays.length} gün · {stripFlat.length} fotoğraf — eskiden yeniye</span>
            </div>
            {stripDays.length === 0 ? (
              <div className="px-6 py-10 text-center text-slate-400 text-sm" data-testid="compare-strip-empty">Bu proje için henüz fotoğraf yok.</div>
            ) : (
              <div className="overflow-x-auto pb-2">
                <div className="flex gap-0 min-w-max">
                  {(() => {
                    let fi = 0;
                    return stripDays.map(([day, items], di) => {
                      const dayPhotoCount = items.reduce((a, ph) => a + (ph.files?.length || 0), 0);
                      const hasOpenIssue = items.some((ph) => ph.is_issue && !ph.resolved);
                      return (
                        <div key={day} className="flex items-stretch" data-testid={`compare-day-${di}`}>
                          <div className="w-[220px] shrink-0 space-y-2">
                            <div className={`border-t-2 pt-2 ${hasOpenIssue ? "border-red-400" : "border-slate-900"}`}>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold bg-slate-900 text-white px-1.5 py-0.5">GÜN {di + 1}</span>
                                {hasOpenIssue && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-red-600"><AlertTriangle size={10} /> sorun</span>}
                              </div>
                              <div className="font-heading text-sm font-extrabold mt-1">{day}</div>
                              <div className="text-[10px] text-slate-400">{dayPhotoCount} fotoğraf</div>
                            </div>
                            <div className="space-y-2">
                              {items.map((ph) => (ph.files || []).map((f) => {
                                const idx = fi++;
                                return (
                                  <button type="button" key={f.id} onClick={() => openStripBox(idx)} data-testid={`compare-thumb-${idx}`} className="relative block w-full aspect-square bg-slate-100 overflow-hidden cursor-zoom-in group">
                                    <img src={`${f.url}?thumb=1`} alt={f.original_filename} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                                    <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] px-1.5 py-0.5 text-left">{timeLabel(ph.created_at)} · {ph.created_by?.name}</span>
                                    {ph.is_issue && (
                                      <span className={`absolute top-1 right-1 p-1 ${ph.resolved ? "bg-emerald-500" : "bg-red-500"} text-white`}>
                                        {ph.resolved ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />}
                                      </span>
                                    )}
                                  </button>
                                );
                              }))}
                            </div>
                          </div>
                          {di < stripDays.length - 1 && (
                            <div className="w-10 shrink-0 flex items-start justify-center pt-3 text-slate-300">
                              <ChevronRight size={18} />
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
          </section>
        ) : (
          <>
        {loaded && groups.length === 0 && (
          <div className="bg-white border border-slate-200 px-6 py-12 text-center text-slate-400" data-testid="field-photos-empty">
            {photos.length > 0 ? "Filtrenizle eşleşen paylaşım yok." : "Henüz saha fotoğrafı yok."}
          </div>
        )}
        {groups.map(([day, items]) => (
          <section key={day} className="space-y-3" data-testid={`field-photo-day-${day}`}>
            <h2 className="font-heading text-lg font-extrabold tracking-tight">{day} <span className="text-sm font-normal text-slate-400">· {items.length} paylaşım</span></h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.map((ph) => (
                <div key={ph.id} className={`bg-white border p-4 ${ph.is_issue ? (ph.resolved ? "border-emerald-300" : "border-amber-300") : "border-slate-200"}`} data-testid={`field-photo-card-${ph.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-sm text-slate-800">{ph.project?.name || "—"}{ph.project?.stand_no ? ` — ${ph.project.stand_no}` : ""}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{timeLabel(ph.created_at)} · {ph.created_by?.name}{ph.created_by?.company ? ` · ${ph.created_by.company}` : ""}{ph.sent_to?.length > 0 ? ` · ${ph.sent_to.length} kişiye gönderildi` : ""}</div>
                    </div>
                    {ph.can_delete && (
                      <button type="button" onClick={() => del(ph)} data-testid={`field-photo-delete-${ph.id}`} className="text-slate-300 hover:text-red-600 transition-colors duration-150"><Trash2 size={14} /></button>
                    )}
                  </div>
                  {ph.is_issue && ph.issue_text && (
                    issueEdit?.id === ph.id ? (
                      <div className="mt-2 space-y-2">
                        <Textarea value={issueEdit.text} onChange={(e) => setIssueEdit({ id: ph.id, text: e.target.value })} data-testid={`field-photo-issue-edit-text-${ph.id}`} className="rounded-none border-amber-300 bg-amber-50/40 min-h-[70px] text-xs" />
                        <div className="flex gap-2">
                          <button type="button" onClick={saveIssue} data-testid={`field-photo-issue-save-${ph.id}`} className="text-[11px] font-bold bg-slate-900 text-white px-3 py-1.5 hover:bg-slate-700 transition-colors duration-150">Kaydet ve E-posta Gönder</button>
                          <button type="button" onClick={() => setIssueEdit(null)} data-testid={`field-photo-issue-cancel-${ph.id}`} className="text-[11px] text-slate-400 hover:text-slate-700 underline">Vazgeç</button>
                        </div>
                      </div>
                    ) : (
                      <div className={`mt-2 text-xs border px-3 py-2 flex gap-1.5 items-start ${ph.resolved ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                        {ph.resolved ? <CheckCircle2 size={13} className="shrink-0 mt-0.5" /> : <AlertTriangle size={13} className="shrink-0 mt-0.5" />}
                        <span className="flex-1">{ph.issue_text}</span>
                        {ph.can_resolve && (
                          <button type="button" onClick={() => setIssueEdit({ id: ph.id, text: ph.issue_text })} data-testid={`field-photo-issue-edit-${ph.id}`} title="Sorunu düzenle" className="shrink-0 text-slate-400 hover:text-slate-800 transition-colors duration-150"><Pencil size={12} /></button>
                        )}
                      </div>
                    )
                  )}
                  {ph.is_issue && (
                    <div className="mt-2 flex items-center flex-wrap gap-2">
                      {ph.resolved ? (
                        <>
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1" data-testid={`field-photo-resolved-badge-${ph.id}`}>
                            <CheckCircle2 size={12} /> Çözüldü{ph.resolved_by?.name ? ` · ${ph.resolved_by.name}` : ""}{ph.resolved_at ? ` · ${dateTimeLabel(ph.resolved_at)}` : ""}
                          </span>
                          {ph.can_resolve && (
                            <button type="button" onClick={() => resolve(ph, false)} data-testid={`field-photo-unresolve-${ph.id}`} className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-700 underline">
                              <Undo2 size={11} /> Geri al
                            </button>
                          )}
                        </>
                      ) : ph.can_resolve ? (
                        <button type="button" onClick={() => resolve(ph, true)} data-testid={`field-photo-resolve-${ph.id}`} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 border border-emerald-300 bg-white hover:bg-emerald-50 px-2.5 py-1.5 transition-colors duration-150">
                          <CheckCircle2 size={13} /> Çözüldü Olarak İşaretle
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1">
                          <AlertTriangle size={12} /> Açık Sorun
                        </span>
                      )}
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {(ph.files || []).map((f, idx) => (
                      <button type="button" key={f.id} onClick={() => openBox(ph, idx)} data-testid={`field-photo-thumb-${ph.id}-${idx}`} className="block aspect-square bg-slate-100 overflow-hidden cursor-zoom-in">
                        <img src={`${f.url}?thumb=1`} alt={f.original_filename} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-200" />
                      </button>
                    ))}
                  </div>
                  <CommentThread ph={ph} onAdd={addComment} />
                </div>
              ))}
            </div>
          </section>
        ))}
          </>
        )}
        </>
        )}
      </main>
      {box && <Lightbox box={box} onClose={() => setBox(null)} onNav={navBox} />}
    </div>
  );
}
